import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { lastProjects } from "resources/projects";
import { goToMainBase } from "ducks/location/location";
import { openBottomContainer } from "ducks/layout/layout";
import { getAppConfig } from "ducks/appConfig";
import { getLocation } from "ducks/location/selectors";
import { createTemporaryProject } from "ducks/project/project";
import { useUser } from "hooks/user/useUser";
import { useTableLayerId } from "../table/useTableLayerId";
import { useIsShared } from "./useIsShared";

import { RouteName, RoutePanelName } from "../../appConfig/types";

const isLocationWithLastProject = (locationType: string) => locationType !== RouteName.Feedback;

export const useLastProjectsEffect = (isConnectedWithServer: boolean) => {
  const dispatch = useDispatch();
  const user = useUser();
  const [, setTableLayerId] = useTableLayerId();
  const isShared = useIsShared();
  const appConfig = useSelector(getAppConfig);
  const [getLastProjects, { data: projects, loading }] = lastProjects.use();
  const location = useSelector(getLocation);
  const [possibleToRedirect, setPossibleToRedirect] = useState<boolean>(false);
  const [gotLastProject, setGotLastProject] = useState(false);

  useEffect(() => {
    setPossibleToRedirect(location.type === RouteName.Main || location.type === RouteName.Catalog);
  }, []);

  useEffect(() => {
    if (!isConnectedWithServer || !user || isShared || !isLocationWithLastProject(location.type)) {
      return;
    }

    if (user.isOpenLastProject) {
      getLastProjects({
        input: {
          input: void 0,
          waitInput: true,
        },
      }).finally(() => {
        setGotLastProject(true);
      });
    } else if (location.type === RouteName.Main && !location.payload?.project && !location.payload?.sharedMap) {
      if (appConfig?.settings?.defaultProject) {
        dispatch(goToMainBase({ project: appConfig?.settings?.defaultProject, panel: RoutePanelName.Layers }));
      } else {
        dispatch(createTemporaryProject());
      }
    }
  }, [isConnectedWithServer, isShared, user, appConfig?.settings?.defaultProject]);

  useEffect(() => {
    if (gotLastProject && !projects?.length) {
      if (appConfig?.settings?.defaultProject) {
        dispatch(goToMainBase({ project: appConfig?.settings?.defaultProject, panel: RoutePanelName.Layers }));
      } else {
        dispatch(createTemporaryProject());
      }
    }
  }, [gotLastProject, appConfig?.settings?.defaultProject]);

  useEffect(() => {
    if (!loading && projects && projects[0] && possibleToRedirect) {
      const projectName = projects[0].name;
      const { payload, query } = location;

      if (location.payload.panel === RoutePanelName.Table && query?.layerName) {
        setTableLayerId(query?.layerName);
        dispatch(openBottomContainer());
      } else if (payload.panel) {
        dispatch(goToMainBase({ project: projectName, panel: payload.panel, query }));
      } else {
        dispatch(goToMainBase({ project: projectName, panel: RoutePanelName.Layers }));
      }
    }
  }, [possibleToRedirect, projects, loading]);
};
